import { httpsCallable, HttpsCallableResult } from "firebase/functions";
import { sendPasswordResetEmail as firebaseSendPasswordResetEmail } from "firebase/auth";
import { functions, auth } from "./firebase";
import { FirebaseUser } from "../types/user";

export async function getAdminSpecialties(): Promise<Array<{ id: string; name: string }>> {
  return (await httpsCallable<unknown, { specialties: Array<{ id: string; name: string }> }>(
    functions, "getAdminSpecialties",
  )({})).data.specialties;
}

export async function listAdminUsers(): Promise<FirebaseUser[]> {
  const users: FirebaseUser[] = [];
  let pageToken: string | null = null;
  do {
    const response: HttpsCallableResult<{
      users: FirebaseUser[];
      nextPageToken: string | null;
    }> = await httpsCallable<
      { pageToken: string | null },
      { users: FirebaseUser[]; nextPageToken: string | null }
    >(
      functions,
      "listAdminUsers",
    )({ pageToken });
    users.push(...response.data.users);
    pageToken = response.data.nextPageToken;
  } while (pageToken);
  return users;
}
export async function getAdminUser(uid: string): Promise<FirebaseUser | null> {
  try {
    return (
      await httpsCallable<{ uid: string }, FirebaseUser>(
        functions,
        "getAdminUser",
      )({ uid })
    ).data;
  } catch (error: any) {
    if (error.code === "functions/not-found") return null;
    throw error;
  }
}
export async function updateAdminUser(data: Record<string, unknown>) {
  return httpsCallable(functions, "updateAdminUser")(data);
}
export async function getUserConsentHistory(
  uid: string,
): Promise<{ records: any[]; limit: number }> {
  return (
    await httpsCallable<{ uid: string }, { records: any[]; limit: number }>(
      functions,
      "getUserConsentHistory",
    )({ uid })
  ).data;
}
export async function getMarketingRecipients(
  uids: string[],
): Promise<string[]> {
  return (
    await httpsCallable<{ uids: string[] }, { emails: string[] }>(
      functions,
      "getMarketingRecipients",
    )({ uids })
  ).data.emails;
}

// Helper function to ensure user is authenticated before calling functions
const ensureAuthenticated = async () => {
  if (!auth.currentUser) {
    throw new Error("User must be authenticated to perform this action");
  }
  // Force token refresh to ensure we have a valid token
  await auth.currentUser.getIdToken(true);
  return auth.currentUser;
};

// Direct callable functions with authentication check
export const sendPasswordResetEmailAuthenticated = async (data: {
  email: string;
}) => {
  await ensureAuthenticated();

  try {
    // Use Firebase Auth's built-in password reset email function
    await firebaseSendPasswordResetEmail(auth, data.email);
    return {
      success: true,
      message: `Password reset email sent to ${data.email}`,
    };
  } catch (error) {
    console.error("Error sending password reset email:", error);
    throw error;
  }
};

export const deleteUserFromAuthAuthenticated = async (data: {
  uid: string;
}) => {
  await ensureAuthenticated();
  const callable = httpsCallable(functions, "deleteUser");
  return await callable(data);
};

export const deleteAllUserDataAuthenticated = async (uid: string) => {
  await ensureAuthenticated();
  const callable = httpsCallable(functions, "deleteAllUserData");
  return await callable({ uid });
};

// Keep the original exports for backward compatibility
export const deleteUserFromAuth = httpsCallable(functions, "deleteUser");
export const deleteAllUserData = httpsCallable(functions, "deleteAllUserData");

// Compatibility alias uses the same authenticated, administrator-checked cleanup.
export const deleteUserViaAPI = deleteAllUserDataAuthenticated;
